import { useId } from 'react';
import type { Material } from '@draughts/shared';
import { resolveBoardMaterial } from './board-materials';

interface BoardSlabProps {
  /** Lato della scacchiera in unità SVG (viewBox), non in pixel. */
  size: number;
  /** Lato di UNA casella nelle stesse unità: è il passo della maschera di parità. */
  squareSize: number;
  /**
   * `true` = casella scura in basso a destra (all'italiana). Stessa convenzione di
   * `isPlayableSquare`, ed è l'unica cosa che decide quale dei due file finisce sulle
   * caselle chiare.
   */
  darkSquareBottomRight: boolean;
  material: Material;
  /** Origine, per le scacchiere che non partono da (0,0). */
  x?: number;
  y?: number;
  /** Altezza, se il riquadro non è quadrato (l'anteprima delle impostazioni è 6x2). */
  height?: number;
  /**
   * Moltiplicatore dell'opacità. SOLO per le pastiglie del menu grafici, dove serve a
   * staccare le quattro voci l'una dall'altra a 26px. Là non c'è nessun aiuto di mossa da
   * proteggere, quindi l'invariante di `board-materials` non si applica — ma ogni punto
   * sopra 1 è una pastiglia che promette un materiale più marcato di quello che la partita
   * rende, e va speso solo se serve davvero. Vedi `boardMaterialPreview`.
   */
  opacityScale?: number;
  /**
   * Usa le venature a 128² invece di quelle a piena risoluzione.
   *
   * SOLO per le pastiglie del selettore di materiale, che mostrano tutti e quattro i
   * materiali insieme: senza, aprire «Grafica» scaricherebbe un megabyte e mezzo per
   * disegnare quattro quadratini da 26px. Ogni altro chiamante — scacchiera, replay,
   * editor, anteprima grande — deve mostrare la venatura vera. Vedi
   * `MATERIAL_TEXTURES_PREVIEW`.
   */
  preview?: boolean;
}

/**
 * La venatura del materiale, come DUE LASTRE CONTINUE: la texture chiara sulle caselle
 * chiare, la scura sulle scure, ciascuna stesa sotto TUTTA la scacchiera e ritagliata
 * sulla propria parità.
 *
 * È così che è fatta una scacchiera intarsiata vera — due tavole sole, tagliate a quadri
 * e alternate — ed è ciò che dà la CONTINUITÀ: la venatura attraversa le caselle della
 * stessa parità invece di ripartire a ogni casella, e una vena si può seguire in
 * diagonale da un quadro al successivo.
 *
 * A CHE SCALA, ed è la sola cosa che questo componente decide da sé. Un materiale con
 * `grainSquares` (il legno) ha il telo largo QUEL numero di caselle e lo si affianca a sé
 * stesso; un materiale senza (il marmo) tiene il comportamento storico, un telo solo steso
 * su tutta la lastra. La differenza si vede confrontando l'anteprima delle impostazioni
 * con la scacchiera vera: con il telo ancorato alla lastra gli stessi 1024 punti del file
 * cadono su 4 caselle di là e su 10 di qua, e l'anteprima mostra una venatura che la
 * partita poi non rende. Vedi `grainSquares` in board-materials.ts per i numeri.
 *
 * Il ritaglio è una maschera costruita su un `<pattern>` di 2x2 caselle, non un rect per
 * casella: sono quattro nodi invece di cento su una 10x10, e il costo non cresce col lato.
 *
 * Va montata SUBITO DOPO le caselle e PRIMA di ogni evidenziazione: sopra i colori di
 * casella, ma sotto aiuti di mossa, ultima mossa e pezzi, che non devono essere velati.
 *
 * Se un file non carica (404, o primo avvio offline prima che il service worker l'abbia
 * precaricato) l'`<image>` semplicemente non disegna nulla e la scacchiera resta ai suoi
 * colori: il degrado è pulito e non serve un ripiego.
 */
export default function BoardSlab({
  size,
  squareSize,
  darkSquareBottomRight,
  material,
  x = 0,
  y = 0,
  height,
  opacityScale = 1,
  preview = false,
}: BoardSlabProps) {
  // Prima di ogni return anticipato: è una regola degli hook. `useId` produce id con i
  // due punti, che in `url(#...)` non sono maneggevoli.
  const uid = useId().replace(/[^\w-]/g, '');

  const spec = resolveBoardMaterial(material);
  // `?? spec.textures` e non un ramo: un materiale con venatura ha SEMPRE le due coppie
  // (l'invariante sta nello spec), ma se un giorno ne mancasse una la pastiglia degrada
  // sulla venatura vera invece di sparire.
  const textures = preview ? spec.texturesPreview ?? spec.textures : spec.textures;
  const h = height ?? size;
  const opacity = Math.min(1, spec.opacity * opacityScale);

  if (!textures) {
    // Vetro: un overlay uniforme, nessuna parità da distinguere. Plastica: niente.
    if (!spec.fill) return null;
    return (
      <g className="board-slab" pointerEvents="none">
        <rect x={x} y={y} width={size} height={h} fill={spec.fill} opacity={opacity} />
      </g>
    );
  }

  const evenPatId = `slab-pat-even-${uid}`;
  const oddPatId = `slab-pat-odd-${uid}`;
  const evenMaskId = `slab-mask-even-${uid}`;
  const oddMaskId = `slab-mask-odd-${uid}`;

  // `isPlayableSquare(r+1, c+1, n, italiana)` vale `(r+c) % 2 === (italiana ? 0 : 1)`
  // con r,c a base zero. Il tassello "pari" accende le celle con (r+c) pari, che sono
  // dunque le SCURE solo sulla scacchiera all'italiana.
  //
  // I ribaltamenti non hanno bisogno di un ramo: `.game-board.flipped` ruota di 180°
  // l'intero <svg> in CSS, e chi specchia in JS lo fa con `n+1-row`, che su lato PARI
  // conserva la parità — `(n+1-r) + (n+1-c) = 2n+2 - (r+c)`. Ogni scacchiera di dama
  // ha lato pari.
  const darkMaskId = darkSquareBottomRight ? evenMaskId : oddMaskId;
  const lightMaskId = darkSquareBottomRight ? oddMaskId : evenMaskId;

  const parityTile = squareSize * 2;

  /**
   * Il TELO, e il periodo con cui si ripete.
   *
   * `grainSquares === null` (marmo) tiene il telo unico steso sulla lastra: `T` è la
   * lastra stessa, il periodo non serve e più sotto si prende il ramo a `<image>` piena.
   *
   * Altrimenti il periodo è DUE teli, non uno, perché i teli si affiancano A SPECCHIO.
   * Serve: i file non sono ripetibili senza salto — il periodo dell'anello (40) non divide
   * i 1024 del file, quindi una ripetizione semplice metterebbe uno scarto di fase su una
   * riga verticale a ogni giunzione — mentre allo specchio i due lati dell'asse mostrano la
   * STESSA colonna del file e combaciano per costruzione. È poi ciò che si fa con
   * un'impiallacciatura vera, dove si chiama book-match.
   */
  const T = spec.grainSquares === null ? size : squareSize * spec.grainSquares;
  const period = T * 2;

  return (
    <g className="board-slab" pointerEvents="none">
      <defs>
        <pattern id={evenPatId} patternUnits="userSpaceOnUse" x={x} y={y} width={parityTile} height={parityTile}>
          <rect x={0} y={0} width={squareSize} height={squareSize} fill="#fff" />
          <rect x={squareSize} y={squareSize} width={squareSize} height={squareSize} fill="#fff" />
        </pattern>
        <pattern id={oddPatId} patternUnits="userSpaceOnUse" x={x} y={y} width={parityTile} height={parityTile}>
          <rect x={squareSize} y={0} width={squareSize} height={squareSize} fill="#fff" />
          <rect x={0} y={squareSize} width={squareSize} height={squareSize} fill="#fff" />
        </pattern>
        {/* Maschera di LUMINANZA: bianco mostra, e il fondo non serve dipingerlo di nero
            perché alpha 0 vale già zero. */}
        <mask id={evenMaskId} maskUnits="userSpaceOnUse" x={x} y={y} width={size} height={h}>
          <rect x={x} y={y} width={size} height={h} fill={`url(#${evenPatId})`} />
        </mask>
        <mask id={oddMaskId} maskUnits="userSpaceOnUse" x={x} y={y} width={size} height={h}>
          <rect x={x} y={y} width={size} height={h} fill={`url(#${oddPatId})`} />
        </mask>
      </defs>

      {/* L'opacità sta sulle due velature e non sul gruppo: le maschere sono complementari
          e non si sovrappongono mai, quindi il risultato è identico ma senza aprire un
          terzo buffer fuori schermo per il gruppo. */}
      <GrainFill
        className="board-slab-texture board-slab-light"
        href={textures.light}
        patternId={`slab-grain-light-${uid}`}
        tiled={spec.grainSquares !== null}
        x={x}
        y={y}
        width={size}
        height={h}
        tile={T}
        period={period}
        opacity={opacity}
        maskId={lightMaskId}
      />
      <GrainFill
        className="board-slab-texture board-slab-dark"
        href={textures.dark}
        patternId={`slab-grain-dark-${uid}`}
        tiled={spec.grainSquares !== null}
        x={x}
        y={y}
        width={size}
        height={h}
        tile={T}
        period={period}
        opacity={opacity}
        maskId={darkMaskId}
      />
    </g>
  );
}

interface GrainFillProps {
  className: string;
  /** Il file di venatura. */
  href: string;
  patternId: string;
  /** `false` = un telo solo steso sul riquadro; `true` = teli da `tile` affiancati. */
  tiled: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  /** Lato del telo. */
  tile: number;
  /** Periodo della tassellatura: due teli, perché si affiancano a specchio. */
  period: number;
  opacity: number;
  maskId: string;
}

/**
 * Una delle due velature: il file di venatura steso sul riquadro e ritagliato sulla
 * propria parità.
 *
 * Senza tassellatura è una `<image>` sola. `slice` conta solo per il riquadro 6x2 delle
 * impostazioni, dove ritaglia invece di schiacciare la venatura; su un riquadro quadrato
 * coincide con `meet`.
 *
 * Con la tassellatura è un `<rect>` riempito da un `<pattern>` che contiene QUATTRO copie
 * del file: dritta, specchiata in x, specchiata in y, specchiata in entrambe. Le copie
 * specchiate combaciano con la dritta lungo tutti i bordi (di là e di qua dell'asse c'è la
 * stessa colonna del file), quindi la tassellatura non ha giunzioni visibili — ha ASSI DI
 * SIMMETRIA, che è un'altra cosa e su un legno si legge come impiallacciatura.
 *
 * L'ORIGINE È SFASATA DI MEZZO TELO, e non è un vezzo. Una tassellatura a specchio è
 * simmetrica rispetto a ogni asse di telo: con l'origine a filo di scacchiera, una 10x10
 * — che con `grainSquares: 5` misura esattamente due teli — si prenderebbe un asse sulla
 * propria MEZZERIA, e la metà sinistra del legno diventerebbe il riflesso esatto della
 * destra proprio dove l'occhio sta. Sfasando, gli assi cadono a due caselle e mezzo dai
 * bordi e nessuna riflessione della scacchiera coincide più con uno di essi.
 *
 * Quattro `<image>` invece di una, ma con lo STESSO href, e il conto dei nodi resta fisso
 * col crescere del lato — che è l'invariante che questo componente si è dato con le
 * maschere di parità.
 *
 * QUI STAVA SCRITTO che «il browser rasterizza il file una volta per dimensione d'uso»,
 * ed è la frase che ha autorizzato a mettere quattro copie di un grafo di filtri dentro un
 * pattern. Chromium mette in cache il paint record dell'immagine SVG, non il suo raster:
 * un filtro dentro il file veniva rieseguito a OGNI rasterizzazione, quattro volte per
 * velatura, dentro il buffer di una maschera, a ogni frame. Quattro `<image>` vanno bene
 * ora che l'href è un raster cotto (`MATERIAL_TEXTURES`), che è un blit; non andavano bene
 * prima, e non andrebbero bene se qualcuno ripuntasse questo href a un SVG con un filtro.
 */
function GrainFill({
  className,
  href,
  patternId,
  tiled,
  x,
  y,
  width,
  height,
  tile,
  period,
  opacity,
  maskId,
}: GrainFillProps) {
  if (!tiled) {
    return (
      <image
        className={className}
        href={href}
        x={x}
        y={y}
        width={width}
        height={height}
        preserveAspectRatio="xMidYMid slice"
        opacity={opacity}
        mask={`url(#${maskId})`}
      />
    );
  }

  const t = tile;
  return (
    <>
      <defs>
        <pattern
          id={patternId}
          patternUnits="userSpaceOnUse"
          x={x - t / 2}
          y={y - t / 2}
          width={period}
          height={period}
        >
          <image href={href} x={0} y={0} width={t} height={t} />
          <image href={href} x={0} y={0} width={t} height={t} transform={`translate(${period} 0) scale(-1 1)`} />
          <image href={href} x={0} y={0} width={t} height={t} transform={`translate(0 ${period}) scale(1 -1)`} />
          <image
            href={href}
            x={0}
            y={0}
            width={t}
            height={t}
            transform={`translate(${period} ${period}) scale(-1 -1)`}
          />
        </pattern>
      </defs>
      <rect
        className={className}
        x={x}
        y={y}
        width={width}
        height={height}
        fill={`url(#${patternId})`}
        opacity={opacity}
        mask={`url(#${maskId})`}
      />
    </>
  );
}
